import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type CSSProperties,
    type KeyboardEvent as ReactKeyboardEvent,
    type ReactNode
} from 'react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { TruncatedLabel } from '../shared/TruncatedLabel';

export type PresentationSelectPlacement = 'trigger' | 'option';

export interface PresentationSelectTone {
    /** Border, background and text classes that replace the trigger's neutral look. */
    readonly trigger: string;
    readonly chevron: string;
    /** For colours that only exist as values (e.g. hashed hex team colours), not Tailwind classes. */
    readonly style?: CSSProperties;
}

export interface PresentationSelectOption<T extends string = string> {
    readonly value: T;
    readonly label: string;
    /** Leading visual for this row (swatch, icon, glyph) - shown in the trigger and every option row. */
    readonly render?: (placement: PresentationSelectPlacement) => ReactNode;
    /** Extra classes for the option row background (e.g. a theme preset's tint). */
    readonly rowClassName?: string;
    /** Tints the whole trigger while this option is selected, like the priority selector. */
    readonly triggerTone?: PresentationSelectTone;
}

interface PresentationSelectProps<T extends string> {
    readonly id: string;
    readonly value: T;
    readonly options: readonly PresentationSelectOption<T>[];
    readonly onChange: (value: T) => void;
    readonly disabled?: boolean;
    /** 'sm' for tight rows (e.g. the Manage VAs controls); defaults to the form-input size. */
    readonly size?: PresentationSelectSize;
}

export type PresentationSelectSize = 'md' | 'sm';

const TRIGGER_BASE_CLASS =
    'flex w-full items-center justify-between gap-2 rounded-md border text-left shadow-sm transition-colors focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed';

const SIZE_CLASSES: Readonly<Record<PresentationSelectSize, { trigger: string; row: string }>> = {
    md: { trigger: 'px-3 py-2 text-sm', row: 'px-3 py-2 text-sm' },
    sm: { trigger: 'px-2.5 py-1.5 text-xs', row: 'px-2.5 py-1.5 text-xs' }
};

const NEUTRAL_TONE: PresentationSelectTone = {
    trigger: 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:hover:bg-white',
    chevron: 'text-gray-400'
};

const LIST_CLASS =
    'absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-[10px] border border-gray-200 bg-white py-1 shadow-[0_10px_28px_rgba(0,0,0,0.13)] focus:outline-none';

// A native <select> can't render a swatch or icon in its options, so this listbox owns open/close,
// keyboard navigation and ARIA; the trigger is a real <button> so a <fieldset disabled> disables it.
export function PresentationSelect<T extends string>({
    id,
    value,
    options,
    onChange,
    disabled,
    size = 'md'
}: PresentationSelectProps<T>) {
    const sizeClasses = SIZE_CLASSES[size];
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const optionRefs = useRef<Array<HTMLLIElement | null>>([]);

    const selectedIndex = options.findIndex((option) => option.value === value);
    const current = selectedIndex >= 0 ? options[selectedIndex] : undefined;
    const tone = current?.triggerTone ?? NEUTRAL_TONE;

    const closeMenu = useCallback((refocusTrigger: boolean) => {
        setOpen(false);
        if (refocusTrigger) triggerRef.current?.focus();
    }, []);

    const openMenu = useCallback(() => {
        if (disabled) return;
        setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
        setOpen(true);
    }, [disabled, selectedIndex]);

    // A read-only user can't reopen the menu, but if `canManage` flips while
    // it's already open, close it rather than leaving a live listbox behind.
    useEffect(() => {
        if (disabled) setOpen(false);
    }, [disabled]);

    useEffect(() => {
        if (open) listRef.current?.focus();
    }, [open]);

    useEffect(() => {
        if (!open) return;
        optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
    }, [open, activeIndex]);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                closeMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open, closeMenu]);

    const selectActive = useCallback(() => {
        const option = options[activeIndex];
        if (!option) return;
        onChange(option.value);
        closeMenu(true);
    }, [options, activeIndex, onChange, closeMenu]);

    const handleListKeyDown = (e: ReactKeyboardEvent<HTMLUListElement>) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex((i) => Math.min(i + 1, options.length - 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex((i) => Math.max(i - 1, 0));
                break;
            case 'Home':
                e.preventDefault();
                setActiveIndex(0);
                break;
            case 'End':
                e.preventDefault();
                setActiveIndex(options.length - 1);
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                selectActive();
                break;
            case 'Escape':
                e.preventDefault();
                closeMenu(true);
                break;
            case 'Tab':
                closeMenu(false);
                break;
            default:
                break;
        }
    };

    const handleTriggerKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
        if (disabled || open) return;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Home' || e.key === 'End') {
            e.preventDefault();
            openMenu();
        }
    };

    return (
        <div ref={containerRef} className="relative">
            <button
                id={id}
                ref={triggerRef}
                type="button"
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => (open ? closeMenu(false) : openMenu())}
                onKeyDown={handleTriggerKeyDown}
                className={`${TRIGGER_BASE_CLASS} ${sizeClasses.trigger} ${tone.trigger}`}
                style={tone.style}
            >
                <span className="flex min-w-0 items-center gap-2">
                    {current?.render?.('trigger')}
                    <TruncatedLabel text={current?.label ?? ''} />
                </span>
                <ChevronDownIcon
                    className={`h-4 w-4 flex-shrink-0 ${tone.chevron} transition-transform ${open ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                />
            </button>
            {open && !disabled && (
                <ul
                    ref={listRef}
                    role="listbox"
                    tabIndex={-1}
                    aria-activedescendant={
                        options[activeIndex] ? `${id}-option-${activeIndex}` : undefined
                    }
                    onKeyDown={handleListKeyDown}
                    className={LIST_CLASS}
                >
                    {options.map((option, index) => {
                        const isSelected = option.value === value;
                        const isActive = index === activeIndex;
                        return (
                            <li
                                key={option.value}
                                id={`${id}-option-${index}`}
                                role="option"
                                aria-selected={isSelected}
                                ref={(el) => {
                                    optionRefs.current[index] = el;
                                }}
                                onMouseEnter={() => setActiveIndex(index)}
                                onClick={() => {
                                    onChange(option.value);
                                    closeMenu(true);
                                }}
                                className={`flex cursor-pointer items-center gap-2 text-gray-700 ${sizeClasses.row} ${
                                    isActive ? 'bg-gray-100' : 'hover:bg-gray-50'
                                } ${option.rowClassName ?? ''}`}
                            >
                                <span className="flex min-w-0 flex-1 items-center gap-2">
                                    {option.render?.('option')}
                                    <TruncatedLabel text={option.label} side="right" />
                                </span>
                                {isSelected && (
                                    <CheckIcon
                                        className="h-4 w-4 flex-shrink-0 text-blue-600"
                                        aria-hidden="true"
                                    />
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
